import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PathwayTypeToLabel } from '@app/pathway-shared';
import { _className, ComponentSync, LL_H_C, LL_V_L, ModuleFE_Toaster, openContent } from '@nu-art/thunderstorm-frontend';
import { asArray, exists } from '@nu-art/ts-common';
import { ModuleFE_Pathway } from '../../../../../_entity.js';
import './Renderer_PathwayHeader.scss';
import { ModuleFE_Tag, ModuleFE_Variable } from '@app/hcs-frontend';
import { Fragment } from 'react';
import { canEditPathway, UtilObject_Pathway, WorkHubItem_Pathway } from '../../../../../search/items/pathway.js';
import { ICONS } from '@app/styles';
import { Component_EntityTimestamps } from '@app/styles/frontend/ui/components/Component_EntityTimestamps/Component_EntityTimestamps';
import { guardResolutionAccordionUnsaved } from '../shared/resolution-accordion-guard.js';
import { getPathwayTimestamps } from '../../../../../utils/pathway-timestamps.js';
import { getPathwayHVOrgConflicts } from '../shared/hv-org-conflicts.js';
import { hasPathwayHVOrgConflict } from '../shared/hv-org-conflict-guard.js';
export const Renderer_PathwayHeader_Modules = [];
export class Renderer_PathwayHeader extends ComponentSync {
    // ######################## Life Cycle ########################
    __onPathwayUpdated = (...params) => {
        const currentPathwayId = this.state.pathway?._id;
        const pathways = asArray(params[1]);
        const newPathway = pathways.find(item => item._id === currentPathwayId);
        if (newPathway)
            this.setState({ pathway: newPathway });
    };
    deriveStateFromProps(nextProps, state) {
        state.renderArgs = nextProps.renderArgs;
        if (state.renderArgs.itemId !== state.pathway?._id)
            state.pathway = ModuleFE_Pathway.cache.unique(state.renderArgs.itemId);
        return state;
    }
    // ######################## Logic ########################
    hasSelectedInnerItem = () => {
        const { stateId, transitionId } = this.state.renderArgs;
        return exists(stateId ?? transitionId);
    };
    deselectInnerItems = async () => {
        if (!this.hasSelectedInnerItem())
            return;
        if (!(await guardResolutionAccordionUnsaved(this.props.tabId)))
            return;
        this.props.workHubItem.updateArgs(this.props.tabId, { stateId: undefined, transitionId: undefined });
    };
    generateSubtitle = () => {
        const pathway = this.state.pathway;
        if (!pathway)
            return;
        const diseaseLabels = pathway.variableIds.map(variableId => ModuleFE_Variable.cache.unique(variableId)?.name ?? 'N/A');
        let str = `${PathwayTypeToLabel[pathway.type]} Pathway`;
        if (diseaseLabels.length)
            str += ` | ${diseaseLabels.join(' - ')}`;
        return str;
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { className: 'pathway-header', children: [this.render_StatusBadges(), this.render_Title(), this.render_Subtitle(), this.render_ParentPointer(), this.render_Tags(), this.render_Timestamps()] });
    }
    render_Timestamps = () => {
        const pathwayId = this.state.pathway?._id;
        if (!pathwayId)
            return null;
        const timestamps = getPathwayTimestamps(pathwayId);
        return exists(timestamps) ? _jsx(Component_EntityTimestamps, { ...timestamps }) : null;
    };
    render_StatusBadges = () => {
        const { pathway } = this.state;
        const archived = pathway?.archived;
        const underConstruction = pathway?.underConstruction;
        if (!archived && !underConstruction)
            return;
        return _jsxs(LL_H_C, { className: 'pathway-header__badges-row', children: [archived && _jsxs(LL_H_C, { className: _className('pathway-header__archived'), children: [_jsx(ICONS.archive.component, {}), "Archived"] }), underConstruction && _jsxs(LL_H_C, { className: _className('pathway-header__under-construction'), children: [_jsx(ICONS.under_construction.component, {}), "Under Construction"] })] });
    };
    render_Title = () => {
        const titleClassName = _className('pathway-header__title', this.hasSelectedInnerItem() && 'clickable');
        return _jsxs(LL_H_C, { className: 'pathway-header__title-row', children: [_jsx("div", { className: titleClassName, onClick: this.deselectInnerItems, children: this.state.pathway?.label ?? 'N/A' }), this.render_EditViewToggle()] });
    };
    render_EditViewToggle = () => {
        const { editMode, readonly, itemId } = this.state.renderArgs;
        if (readonly || !canEditPathway() || !itemId)
            return null;
        const Icon = ICONS.edit.component;
        const className = _className('pathway-header__mode-toggle', editMode ? 'pathway-header__mode-toggle--edit' : 'pathway-header__mode-toggle--view');
        const tooltipLabel = editMode ? 'Switch to View Mode' : 'Switch to Edit Mode';
        return _jsx("div", { className: className, onClick: async () => {
                // Block leaving edit mode while an HV/organization uniqueness conflict is unresolved,
                // otherwise the pathway can't be saved and view mode would hide the conflict banner.
                // Live flag (from Editor_Pathway) catches unsaved edits — e.g. brand-new pathways whose
                // conflicting HV/org never persisted (409); cache check is the fallback for preexisting
                // conflicts when the editor is momentarily unmounted (a state/transition is selected).
                const hasConflict = hasPathwayHVOrgConflict(this.props.tabId)
                    || (!!this.state.pathway && !!getPathwayHVOrgConflicts(this.state.pathway).length);
                if (editMode && hasConflict)
                    return ModuleFE_Toaster.toastError('Resolve the HV / organization conflict before switching to view mode.');
                // Leaving edit mode hides the resolution accordion — guard against losing unsaved changes.
                if (editMode && !(await guardResolutionAccordionUnsaved(this.props.tabId)))
                    return;
                this.props.workHubItem.updateArgs(this.props.tabId, { editMode: !editMode });
            }, ...openContent.tooltip.bottom('pathway-edit-toggle', () => _jsx(_Fragment, { children: tooltipLabel }), { offset: 4 }), children: _jsx(Icon, {}) });
    };
    render_Subtitle = () => {
        const subTitle = this.generateSubtitle();
        if (!subTitle)
            return;
        return _jsx("span", { className: 'pathway-header__sub-title', children: subTitle });
    };
    render_ParentPointer = () => {
        if (!this.state.pathway?._parentId)
            return _jsx(_Fragment, {});
        const parentPathway = ModuleFE_Pathway.cache.unique(this.state.pathway._parentId);
        return _jsxs("span", { className: 'pathway-header__sub-title', children: ["Child of ", _jsxs("i", { className: 'clickable', onClick: () => {
                        if (parentPathway?._id) {
                            WorkHubItem_Pathway.openTab(UtilObject_Pathway.generateTabId(parentPathway._id), parentPathway.label ?? 'N/A', { itemId: parentPathway._id, editMode: false, readonly: this.state.renderArgs.readonly });
                        }
                    }, children: ["$", parentPathway?.label ?? 'N/A'] })] });
    };
    render_Tags = () => {
        if (!this.state.pathway?.tagGroups?.length)
            return;
        const tagLabels = [];
        this.state.pathway.tagGroups.forEach(group => {
            if (!group.value.length)
                return;
            tagLabels.push(group.value.map(tagId => ModuleFE_Tag.cache.unique(tagId)?.label ?? 'N/A').join(' | '));
        });
        return _jsx("span", { className: 'pathway-header__sub-title', children: tagLabels.map((label, index) => {
                return _jsxs(Fragment, { children: [label, (index !== tagLabels.length - 1) && _jsx("br", {})] }, index);
            }) });
    };
}
//# sourceMappingURL=Renderer_PathwayHeader.js.map